/**
 * @fileoverview Generic Extraction Renderer
 *
 * Purpose: Renders category-specific extraction data in a structured layout.
 * Handles nested objects, arrays of objects (as tables), arrays of primitives
 * (as lists), amounts, dates, identifiers, and period objects.
 */

import { useState, type ReactNode } from "react";
import { Badge } from "@/ui/primitives/badge";
import {
  ChevronDown,
  ChevronRight,
  FileText,
  DollarSign,
  Calendar,
  Users,
  Building2,
  Shield,
  Briefcase,
  Receipt,
} from "lucide-react";

interface GenericExtractionRendererProps {
  category: string;
  data: unknown;
}

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------

function Section({
  icon,
  title,
  count,
  defaultOpen = true,
  children,
}: {
  icon: ReactNode;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="-mx-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-muted/30"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </span>
        {count !== undefined && count > 0 && (
          <Badge variant="secondary" size="sm" className="ml-auto">
            {count}
          </Badge>
        )}
      </button>
      {open && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatAmount(amount: number, currency?: string): string {
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const prefix = amount < 0 ? "-" : "";
  return `${prefix}$${formatted}${currency && currency !== "AUD" ? ` ${currency}` : ""}`;
}

function formatDate(date: string): string {
  if (!date) return "";
  try {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return date;
  }
}

const DISPLAY_LABELS: Record<string, string> = {
  previousValue: "Changed From",
  newValue: "Changed To",
};

function humanize(key: string): string {
  if (DISPLAY_LABELS[key]) return DISPLAY_LABELS[key];
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

/** Check if an object looks like an ExtractedAmount { amount, currency, ... } */
function isAmountObject(
  obj: unknown,
): obj is { amount: number; currency?: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "amount" in obj &&
    typeof obj.amount === "number"
  );
}

/** Check if an object looks like a period { from?, to } */
function isPeriodObject(
  obj: unknown,
): obj is Record<string, unknown> & { from?: string; to?: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    ("from" in obj || "to" in obj) &&
    Object.keys(obj).length <= 3
  );
}

/** Check if an object looks like an identifier { type, value } */
function isIdentifierObject(
  obj: unknown,
): obj is { type: string; value: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "type" in obj &&
    "value" in obj &&
    typeof obj.type === "string" &&
    typeof obj.value === "string"
  );
}

/** Check if an object looks like a leave balance { quantity, unit } */
function isLeaveBalanceObject(
  obj: unknown,
): obj is { quantity: number; unit: string } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "quantity" in obj &&
    "unit" in obj &&
    typeof obj.quantity === "number" &&
    typeof obj.unit === "string"
  );
}

// ---------------------------------------------------------------------------
// Value renderers
// ---------------------------------------------------------------------------

function AmountBadge({
  amount,
}: {
  amount: { amount: number; currency?: string };
}) {
  return (
    <span
      className={`font-mono text-xs ${amount.amount < 0 ? "text-red-600" : ""}`}
    >
      {formatAmount(amount.amount, amount.currency)}
    </span>
  );
}

function IdentifierBadge({ id }: { id: { type: string; value: string } }) {
  return (
    <Badge variant="outline" className="gap-1 font-mono text-xs">
      <span className="uppercase text-muted-foreground">{id.type}</span>
      {id.value}
    </Badge>
  );
}

/** Renders a period object like { from: "2024-07-01", to: "2025-06-30" } */
function renderPeriod(obj: { from?: string; to?: string }): ReactNode {
  const { from, to } = obj;
  const parts: string[] = [];
  if (from) parts.push(formatDate(from));
  if (to) parts.push(formatDate(to));
  return <span className="font-mono text-xs">{parts.join(" — ")}</span>;
}

/**
 * Core value renderer — handles all types recursively.
 * This is the key function that fixes [object] and "N items" issues.
 */
function renderValue(value: unknown, depth = 0): ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (typeof value === "boolean") {
    return <span>{value ? "Yes" : "No"}</span>;
  }

  if (typeof value === "number") {
    return <span className="font-mono">{value.toLocaleString("en-AU")}</span>;
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return <span className="font-mono text-xs">{formatDate(value)}</span>;
    }
    if (/^[a-z]+(_[a-z]+)+$/.test(value)) {
      return <span>{humanize(value)}</span>;
    }
    return <span>{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    // Array of strings — render as comma-separated or bulleted list
    if (typeof value[0] === "string") {
      if (value.length <= 3) {
        return <span>{value.join(", ")}</span>;
      }
      return (
        <ul className="list-inside list-disc space-y-0.5 text-xs">
          {value.map((item, i) => (
            <li key={i}>{String(item)}</li>
          ))}
        </ul>
      );
    }

    // Array of numbers
    if (typeof value[0] === "number") {
      return <span className="font-mono">{value.join(", ")}</span>;
    }

    // Array of identifiers
    if (
      typeof value[0] === "object" &&
      value[0] !== null &&
      isIdentifierObject(value[0])
    ) {
      return (
        <div className="flex flex-wrap gap-0.5">
          {value.map((id, i) => (
            <IdentifierBadge
              key={i}
              id={id as { type: string; value: string }}
            />
          ))}
        </div>
      );
    }

    // Array of objects — render compact inline list
    if (typeof value[0] === "object" && depth > 0) {
      return (
        <div className="space-y-1 text-xs">
          {value.map((item, i) => {
            const obj = item as Record<string, unknown>;

            // Appointment-shaped objects → natural language format
            if (
              "role" in obj &&
              ("appointedDate" in obj || "ceasedDate" in obj)
            ) {
              const role = humanize(String(obj.role ?? ""));
              const appointed = obj.appointedDate
                ? formatDate(String(obj.appointedDate))
                : null;
              const ceased = obj.ceasedDate
                ? formatDate(String(obj.ceasedDate))
                : null;
              const parts = [role];
              if (appointed) parts.push(`Appointed on ${appointed}`);
              if (ceased) parts.push(`Ceased on ${ceased}`);
              return <div key={i}>{parts.join(". ")}</div>;
            }

            // Address-shaped objects → simple "fullAddress" display
            if ("fullAddress" in obj && "type" in obj) {
              return <div key={i}>{String(obj.fullAddress ?? "")}</div>;
            }

            const entries = Object.entries(obj).filter(
              ([k, v]) =>
                v !== null &&
                v !== undefined &&
                v !== "" &&
                k !== "source" &&
                !HIDDEN_KEYS.has(k),
            );
            if (entries.length === 0) return null;

            if (entries.length <= 3) {
              return (
                <div key={i}>
                  {entries.map(([k, v], j) => (
                    <span key={k}>
                      {j > 0 && ", "}
                      <span className="text-muted-foreground">
                        {humanize(k)}:
                      </span>{" "}
                      {renderValue(v, depth + 1)}
                    </span>
                  ))}
                </div>
              );
            }

            return (
              <div
                key={i}
                className="space-y-0.5 rounded border border-border/50 p-1.5"
              >
                {entries.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4">
                    <span className="shrink-0 text-muted-foreground">
                      {humanize(k)}
                    </span>
                    <span className="text-right">
                      {renderValue(v, depth + 1)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      );
    }

    // Top-level array of objects — handled by renderArraySection
    return (
      <span className="text-muted-foreground">{value.length} entries</span>
    );
  }

  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;

    // Amount object
    if (isAmountObject(obj)) {
      return <AmountBadge amount={obj} />;
    }

    // Period object
    if (isPeriodObject(obj)) {
      return renderPeriod(obj);
    }

    // Identifier object
    if (isIdentifierObject(obj)) {
      return <IdentifierBadge id={obj} />;
    }

    // Leave balance object
    if (isLeaveBalanceObject(obj)) {
      const qty = obj.quantity.toLocaleString("en-AU");
      return (
        <span className="font-mono text-xs">
          {qty} {obj.unit}
        </span>
      );
    }

    // Generic object — render key-value pairs inline (skip zero counts)
    const entries = Object.entries(obj).filter(
      ([k, v]) =>
        v !== null &&
        v !== undefined &&
        v !== "" &&
        v !== 0 &&
        k !== "source" &&
        !HIDDEN_KEYS.has(k),
    );

    if (entries.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    // For simple objects (2-3 fields), render inline
    if (entries.length <= 3 && depth > 0) {
      return (
        <span className="text-xs">
          {entries.map(([k, v], i) => (
            <span key={k}>
              {i > 0 && ", "}
              <span className="text-muted-foreground">{humanize(k)}:</span>{" "}
              {renderValue(v, depth + 1)}
            </span>
          ))}
        </span>
      );
    }

    // For larger objects, render as mini key-value block
    return (
      <div className="space-y-0.5 text-xs">
        {entries.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4">
            <span className="shrink-0 text-muted-foreground">
              {humanize(k)}
            </span>
            <span className="text-right">{renderValue(v, depth + 1)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Final fallback — safely stringify anything we don't recognise
  try {
    return (
      <span className="text-xs text-muted-foreground">
        {JSON.stringify(value)}
      </span>
    );
  } catch {
    return <span className="text-muted-foreground">-</span>;
  }
}

// ---------------------------------------------------------------------------
// Column ordering for specific sections
// ---------------------------------------------------------------------------

const COLUMN_ORDER: Record<string, string[]> = {
  relatedEntities: [
    "name",
    "identifiers",
    "relationship",
    "relationshipDescription",
  ],
  notedEntities: ["name", "identifiers", "role", "roleDescription"],
  officers: ["fullName", "dateOfBirth", "birthLocality", "appointments"],
  shareholders: ["entityName", "holderName", "holderType", "holdings"],
  changeHistory: [
    "effectiveDate",
    "changeType",
    "description",
    "previousValue",
    "newValue",
  ],
  registrations: [
    "registrationNumber",
    "securedParty",
    "grantor",
    "securityType",
    "collateralClass",
    "collateralType",
    "collateralDescription",
    "registrationDate",
    "registrationEndDate",
    "isPMSI",
    "isTransitional",
  ],
  employees: [
    "name",
    "startDate",
    "terminationDate",
    "employmentCategory",
    "annualSalary",
    "averageWeeklyHours",
    "wageRate",
    "payFrequency",
    "residencyStatus",
    "tfnPresent",
  ],
  entitlements: [
    "employeeName",
    "entitlementType",
    "leaveBalance",
    "amount",
    "asAtDate",
  ],
  creditorDetails: ["name", "abn", "address", "email"],
  creditor: ["name", "abn", "address", "email"],
  // Raw transaction data — show key financial columns first
  transactions: [
    "date",
    "description",
    "amount",
    "direction",
    "debit",
    "credit",
    "balanceAfter",
    "reference",
    "counterpartyName",
    "transactionType",
  ],
  rawEntries: [
    "date",
    "accountCode",
    "accountName",
    "description",
    "debit",
    "credit",
    "balance",
    "reference",
    "journal",
  ],
};

/**
 * Nested keys that should be rendered right after Primary Entity (before
 * Key Dates and Key Amounts) for specific categories.
 */
const PROMOTED_NESTED_KEYS: Record<string, string[]> = {
  general_liabilities: ["creditorDetails", "creditor", "creditorInfo"],
};

/**
 * Controls the order of section keys within nested data objects.
 * Keys listed here appear first (in order); remaining keys follow.
 */
const NESTED_KEY_ORDER: Record<string, string[]> = {
  general_liabilities: [
    "creditorDetails",
    "creditor",
    "creditorInfo",
    "items",
    "agreementTerms",
    "statedTotals",
  ],
};

/**
 * Keys to suppress from rendering. These are commonly hallucinated by the
 * LLM and are not part of any extraction schema. Matched case-insensitively
 * against nested keys within category-specific data.
 */
const HIDDEN_KEYS = new Set([
  "relatedPartyStatus",
  "relatedPartyDetails",
  "isRelatedCreditor",
  "isRelatedParty",
  "relatedCreditor",
  "relationship",
  "relatedPartyAssessment",
]);

/** Sections that should always render as a table (never cards), regardless of column count */
const FORCE_TABLE_SECTIONS = new Set(["transactions", "rawEntries"]);

// Keys to use as card header (displayed prominently at top of card)
const CARD_HEADER_KEYS: Record<string, { title: string; subtitle?: string }> = {
  registrations: { title: "registrationNumber", subtitle: "securedParty" },
};

// Keys to hide when their value is false, 0, empty array, or empty string
const HIDE_FALSY_KEYS = new Set([
  "isPMSI",
  "isTransitional",
  "proceeds",
  "inventory",
  "subordinate",
  "coveredAssets",
  "changeHistory",
]);

function getOrderedKeys(
  sectionKey: string,
  discoveredKeys: string[],
): string[] {
  const preferred = COLUMN_ORDER[sectionKey];
  if (!preferred) return discoveredKeys;

  const ordered = preferred.filter((k) => discoveredKeys.includes(k));
  const remaining = discoveredKeys.filter((k) => !preferred.includes(k));
  return [...ordered, ...remaining];
}

// ---------------------------------------------------------------------------
// Array-of-objects table renderer
// ---------------------------------------------------------------------------

/** Default page size for paginated tables */
const PAGE_SIZE = 100;

/**
 * Renders an array of objects as a table.
 * Filters out 'source' columns.
 * Handles nested values properly via renderValue.
 * For large arrays (>100 items), paginates with "Show more" button.
 */
function ArraySection({
  items,
  label,
  icon,
  sectionKey,
}: {
  items: unknown[];
  label: string;
  icon: ReactNode;
  sectionKey?: string;
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  if (!items || items.length === 0) return null;

  const firstItem = items[0];

  // If array of primitives, render as list
  if (typeof firstItem !== "object" || firstItem === null) {
    return (
      <Section icon={icon} title={label} count={items.length}>
        <div className="space-y-0.5 text-xs">
          {items.map((item, i) => (
            <div key={i}>{renderValue(item, 1)}</div>
          ))}
        </div>
      </Section>
    );
  }

  // Get display keys — exclude source
  const allKeys = new Set<string>();
  for (const item of items) {
    if (typeof item === "object" && item !== null) {
      for (const key of Object.keys(item)) {
        allKeys.add(key);
      }
    }
  }
  const rawKeys = Array.from(allKeys).filter(
    (k) => k !== "source" && !HIDDEN_KEYS.has(k),
  );
  const keys = sectionKey ? getOrderedKeys(sectionKey, rawKeys) : rawKeys;

  // Use table mode if: ≤8 columns OR section is in FORCE_TABLE_SECTIONS
  const forceTable = sectionKey ? FORCE_TABLE_SECTIONS.has(sectionKey) : false;
  const useTable = keys.length <= 8 || forceTable;

  if (!useTable) {
    // Card mode for wide non-transaction data
    const headerConfig = sectionKey ? CARD_HEADER_KEYS[sectionKey] : undefined;

    return (
      <Section
        icon={icon}
        title={label}
        count={items.length}
        defaultOpen={items.length <= 5}
      >
        <div className="space-y-3">
          {items.map((item, idx) => {
            const obj = item as Record<string, unknown>;

            const visibleKeys = keys.filter((k) => {
              const v = obj[k];
              if (v === null || v === undefined || v === "") return false;
              if (HIDE_FALSY_KEYS.has(k) && (v === false || v === 0))
                return false;
              if (HIDE_FALSY_KEYS.has(k) && Array.isArray(v) && v.length === 0)
                return false;
              if (
                headerConfig &&
                (k === headerConfig.title || k === headerConfig.subtitle)
              )
                return false;
              return true;
            });

            const headerTitle = headerConfig
              ? obj[headerConfig.title]
              : undefined;
            const headerSubtitle = headerConfig?.subtitle
              ? obj[headerConfig.subtitle]
              : undefined;

            return (
              <div key={idx} className="space-y-2 rounded-lg border p-3">
                {headerConfig &&
                  (headerTitle !== undefined ||
                    headerSubtitle !== undefined) && (
                    <div className="border-b border-border/50 pb-2">
                      {headerTitle !== undefined && (
                        <div className="text-xs font-semibold">
                          {renderValue(headerTitle, 1)}
                        </div>
                      )}
                      {headerSubtitle !== undefined && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {renderValue(headerSubtitle, 1)}
                        </div>
                      )}
                    </div>
                  )}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  {visibleKeys.map((k) => (
                    <div key={k} className="flex justify-between gap-2 text-xs">
                      <span className="shrink-0 text-muted-foreground">
                        {humanize(k)}
                      </span>
                      <span className="text-right">
                        {renderValue(obj[k], 1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    );
  }

  // Table mode — with pagination for large arrays
  const displayItems =
    items.length > PAGE_SIZE ? items.slice(0, visibleCount) : items;
  const hasMore = visibleCount < items.length;

  // Detect numeric columns for right-alignment
  const numericKeys = new Set<string>();
  const NUMERIC_PATTERNS =
    /^(amount|debit|credit|balance|balanceAfter|gst|gross|total|net|tax|closingBalance)$/i;
  for (const key of keys) {
    if (NUMERIC_PATTERNS.test(key)) {
      numericKeys.add(key);
    } else {
      // Check if most values are numbers
      const sample = displayItems.slice(0, 10);
      const numCount = sample.filter((item) => {
        const v = (item as Record<string, unknown>)[key];
        return typeof v === "number";
      }).length;
      if (numCount > sample.length * 0.5) numericKeys.add(key);
    }
  }

  const ENUM_KEYS = new Set([
    "relationship",
    "role",
    "status",
    "type",
    "changeType",
    "holderType",
    "resolutionType",
    "registrationStatus",
    "direction",
    "transactionType",
    "entry_source",
    "transaction_source",
  ]);

  return (
    <Section
      icon={icon}
      title={label}
      count={items.length}
      defaultOpen={items.length <= 20}
    >
      <div className="overflow-hidden rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="hover:bg-transparent">
                {keys.map((key) => (
                  <th
                    key={key}
                    className={`h-9 whitespace-nowrap bg-muted/40 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${
                      numericKeys.has(key) ? "text-right" : "text-left"
                    }`}
                  >
                    {humanize(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {displayItems.map((item, idx) => {
                const row = item as Record<string, unknown>;
                return (
                  <tr
                    key={idx}
                    className={`border-b border-border/40 transition-colors hover:bg-muted/50 ${
                      idx % 2 === 1 ? "bg-muted/20" : ""
                    }`}
                  >
                    {keys.map((key) => {
                      const isNumeric = numericKeys.has(key);
                      const isEnum = ENUM_KEYS.has(key);
                      return (
                        <td
                          key={key}
                          className={`px-3 py-2 ${
                            isNumeric
                              ? "text-right font-mono tabular-nums"
                              : isEnum
                                ? "whitespace-nowrap"
                                : "max-w-xs"
                          }`}
                        >
                          {renderValue(row[key], 1)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {hasMore && (
        <button
          onClick={() =>
            setVisibleCount((c) => Math.min(c + PAGE_SIZE, items.length))
          }
          className="mt-2 w-full rounded-lg border py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
        >
          Show more (
          {Math.min(PAGE_SIZE, items.length - visibleCount).toLocaleString()} of{" "}
          {(items.length - visibleCount).toLocaleString()} remaining)
        </button>
      )}
    </Section>
  );
}

/** Wrapper for backward compat — renderArraySection returns ReactNode */
function renderArraySection(
  items: unknown[],
  label: string,
  icon: ReactNode,
  sectionKey?: string,
) {
  return (
    <ArraySection
      items={items}
      label={label}
      icon={icon}
      sectionKey={sectionKey}
    />
  );
}

// ---------------------------------------------------------------------------
// Category icon map
// ---------------------------------------------------------------------------

const CATEGORY_ICONS: Record<string, ReactNode> = {
  corporate_documents: (
    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  director_disclosures: <Users className="h-3.5 w-3.5 text-muted-foreground" />,
  assets: <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />,
  securities: <Shield className="h-3.5 w-3.5 text-muted-foreground" />,
  financial_ledgers: (
    <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  bank_statements_transactions: (
    <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  employment: <Users className="h-3.5 w-3.5 text-muted-foreground" />,
  general_liabilities: (
    <Receipt className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  board_minutes_reports: (
    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  loans: <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />,
  asset_sales_purchases: (
    <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
  ),
  tax_and_sro: <Receipt className="h-3.5 w-3.5 text-muted-foreground" />,
};

// ---------------------------------------------------------------------------
// Main renderer
// ---------------------------------------------------------------------------

export function GenericExtractionRenderer({
  category,
  data,
}: GenericExtractionRendererProps) {
  if (!data || typeof data !== "object") return null;

  const extraction = data as Record<string, unknown>;
  const icon = CATEGORY_ICONS[category] ?? (
    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
  );

  const rawSummary = extraction.summary;
  const summary = typeof rawSummary === "string" ? rawSummary : undefined;
  const rawPrimaryEntity = extraction.primaryEntity;
  const primaryEntity =
    rawPrimaryEntity &&
    typeof rawPrimaryEntity === "object" &&
    "name" in rawPrimaryEntity
      ? (rawPrimaryEntity as {
          name: string;
          identifiers?: Array<{ type: string; value: string }>;
        })
      : undefined;
  const dates = Array.isArray(extraction.dates)
    ? (extraction.dates as Array<{
        label: string;
        date: string;
        isApproximate?: boolean;
      }>)
    : undefined;
  const amounts = Array.isArray(extraction.amounts)
    ? (extraction.amounts as Array<{
        label: string;
        amount: number;
        currency?: string;
      }>)
    : undefined;

  // Find category-specific data keys (everything that isn't a BaseExtraction field)
  const baseKeys = new Set([
    "category",
    "extractionSchemaVersion",
    "summary",
    "primaryEntity",
    "dates",
    "amounts",
  ]);
  const categoryDataKeys = Object.keys(extraction).filter(
    (k) => !baseKeys.has(k),
  );

  // Collect promoted nested keys to render right after Primary Entity
  const promotedKeys = PROMOTED_NESTED_KEYS[category] ?? [];
  const promotedSections: ReactNode[] = [];
  const promotedKeySet = new Set<string>();

  if (promotedKeys.length > 0) {
    for (const dataKey of categoryDataKeys) {
      const value = extraction[dataKey];
      if (!value || typeof value !== "object" || Array.isArray(value)) continue;
      const nested = value as Record<string, unknown>;
      for (const pKey of promotedKeys) {
        const pValue = nested[pKey];
        if (pValue === null || pValue === undefined) continue;
        promotedKeySet.add(pKey);
        if (typeof pValue === "object" && !Array.isArray(pValue)) {
          promotedSections.push(
            <div key={`promoted-${pKey}`}>
              {renderNestedData({ [pKey]: pValue }, category)}
            </div>,
          );
        } else if (Array.isArray(pValue) && pValue.length > 0) {
          promotedSections.push(
            <div key={`promoted-${pKey}`}>
              {renderArraySection(
                pValue,
                humanize(pKey),
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />,
                pKey,
              )}
            </div>,
          );
        }
      }
    }
  }

  return (
    <div className="space-y-3">
      {/* Summary */}
      {summary && (
        <Section icon={icon} title="Summary">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {summary}
          </p>
        </Section>
      )}

      {/* Primary Entity */}
      {primaryEntity && primaryEntity.name && (
        <Section
          icon={<Building2 className="h-3.5 w-3.5 text-muted-foreground" />}
          title="Primary Entity"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{primaryEntity.name}</span>
            {primaryEntity.identifiers &&
              primaryEntity.identifiers.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {primaryEntity.identifiers.map((id, i) => (
                    <IdentifierBadge key={i} id={id} />
                  ))}
                </div>
              )}
          </div>
        </Section>
      )}

      {/* Promoted sections (right after Primary Entity) */}
      {promotedSections}

      {/* Key Dates */}
      {dates && dates.length > 0 && (
        <Section
          icon={<Calendar className="h-3.5 w-3.5 text-muted-foreground" />}
          title="Key Dates"
          count={dates.length}
        >
          <div className="space-y-1">
            {dates.map((d, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{d.label}</span>
                <span className="font-mono text-muted-foreground">
                  {formatDate(d.date)}
                  {d.isApproximate && " (approx)"}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Key Amounts */}
      {amounts && amounts.length > 0 && (
        <Section
          icon={<DollarSign className="h-3.5 w-3.5 text-muted-foreground" />}
          title="Key Amounts"
          count={amounts.length}
        >
          <div className="space-y-1">
            {amounts.map((a, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span>{a.label}</span>
                <AmountBadge amount={a} />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Category-specific data */}
      {categoryDataKeys.map((dataKey) => {
        const value = extraction[dataKey];
        if (value === null || value === undefined) return null;

        // If it's an object with nested keys, render each sub-key
        if (typeof value === "object" && !Array.isArray(value)) {
          return (
            <div key={dataKey}>
              {renderNestedData(
                value as Record<string, unknown>,
                category,
                promotedKeySet,
              )}
            </div>
          );
        }

        // If it's a top-level array
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div key={dataKey}>
              {renderArraySection(
                value,
                humanize(dataKey),
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />,
                dataKey,
              )}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

/**
 * Renders a nested data object (like the category-specific payload).
 * Each key becomes a section — arrays become tables, objects become
 * key-value blocks, primitives are rendered inline.
 */
function renderNestedData(
  data: Record<string, unknown>,
  category: string,
  excludeKeys?: Set<string>,
) {
  const sections: ReactNode[] = [];
  const icon = <FileText className="h-3.5 w-3.5 text-muted-foreground" />;

  const rawKeys = Object.keys(data).filter(
    (k) =>
      k !== "entity" &&
      k !== "source" &&
      !HIDDEN_KEYS.has(k) &&
      data[k] !== null &&
      data[k] !== undefined &&
      !excludeKeys?.has(k),
  );

  const preferred = NESTED_KEY_ORDER[category];
  const orderedKeys = preferred
    ? [
        ...preferred.filter((k) => rawKeys.includes(k)),
        ...rawKeys.filter((k) => !preferred.includes(k)),
      ]
    : rawKeys;

  for (const key of orderedKeys) {
    const value = data[key];

    // Array of objects/primitives
    if (Array.isArray(value) && value.length > 0) {
      const section = renderArraySection(value, humanize(key), icon, key);
      if (section)
        sections.push(<div key={`${category}-${key}`}>{section}</div>);
      continue;
    }

    // Nested object — split scalar entries (key-value) from array entries (tables)
    if (typeof value === "object" && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      const entries = Object.entries(obj).filter(
        ([k, v]) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          k !== "source" &&
          !HIDDEN_KEYS.has(k),
      );
      if (entries.length === 0) continue;

      const rawScalarEntries = entries.filter(
        ([, v]) => !Array.isArray(v) && v !== 0,
      );
      const arrayEntries = entries.filter(
        (entry): entry is [string, unknown[]] =>
          Array.isArray(entry[1]) && entry[1].length > 0,
      );

      // Apply key ordering for scalar entries if configured
      const scalarKeyOrder = getOrderedKeys(
        key,
        rawScalarEntries.map(([k]) => k),
      );
      const scalarEntries = scalarKeyOrder.flatMap((k) => {
        const entry = rawScalarEntries.find(([ek]) => ek === k);
        return entry ? [entry] : [];
      });

      // No scalars — render arrays directly without a redundant parent wrapper
      if (scalarEntries.length === 0 && arrayEntries.length > 0) {
        for (const [k, v] of arrayEntries) {
          const title =
            arrayEntries.length === 1
              ? humanize(key)
              : `${humanize(key)} — ${humanize(k)}`;
          const section = renderArraySection(v, title, icon, k);
          if (section)
            sections.push(<div key={`${category}-${key}-${k}`}>{section}</div>);
        }
        continue;
      }

      sections.push(
        <Section
          key={`${category}-${key}`}
          icon={icon}
          title={humanize(key)}
          defaultOpen={arrayEntries.length > 0}
        >
          <div className="space-y-2">
            {scalarEntries.length > 0 && (
              <div className="space-y-1">
                {scalarEntries.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 text-xs">
                    <span className="shrink-0 text-muted-foreground">
                      {humanize(k)}
                    </span>
                    <span className="text-right">{renderValue(v, 1)}</span>
                  </div>
                ))}
              </div>
            )}
            {arrayEntries.map(([k, v]) =>
              renderArraySection(v, humanize(k), icon, k),
            )}
          </div>
        </Section>,
      );
      continue;
    }

    // Primitive values at the top level of category data are skipped —
    // they render without section headings and are typically hallucinated
    // LLM fields not part of the schema.
  }

  return sections.length > 0 ? (
    <div className="space-y-3">{sections}</div>
  ) : null;
}
